import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Link2, Unlink } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtCurrency, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const Picker = ({ onPick }) => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return undefined; }
    const t = setTimeout(() => api.get("/supplier-invoices/orders/search", { params: { q } }).then((r) => setRes(r.data)).catch(() => setRes([])), 300);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="mt-2 rounded-lg border border-line p-2">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca ordine: numero, cliente o descrizione" className="h-8 text-xs" data-testid="supplier-order-search" />
      <div className="mt-1 max-h-48 overflow-y-auto">{res.map((o) => o.lines.map((l) => (
        <button key={l.id} type="button" onClick={() => onPick(o.id, l.id)} className="block w-full rounded px-2 py-1 text-left text-xs hover:bg-slate-50" data-testid={`supplier-order-pick-${l.id}`}>
          <span className="font-semibold">{o.number}</span> · {o.company} · riga {l.position}: {l.code ? `${l.code} ` : ""}{l.description} ({l.quantity})</button>)))}</div>
    </div>
  );
};

const Line = ({ ln, sug, edit, onMatch }) => {
  const [pick, setPick] = useState(false);
  return (
    <li className="py-3" data-testid={`supplier-line-${ln.id}`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1"><p className="text-sm text-ink">{ln.code && <span className="mr-1 font-mono text-xs text-slate-400">{ln.code}</span>}{ln.name}</p>
          <p className="text-xs text-slate-500">{ln.qty} {ln.measure || ""} × {fmtCurrency(ln.net_price)}</p></div>
        <p className="text-sm font-semibold text-ink">{fmtCurrency(ln.net_amount)}</p>
      </div>
      {ln.match ? (
        <div className="mt-1 flex items-center gap-2 text-xs text-emerald-700" data-testid={`supplier-line-match-${ln.id}`}><Link2 className="h-3.5 w-3.5" />
          <Link to={`/ordini/${ln.match.order_id}`} className="font-semibold hover:underline">{ln.match.order_number} · riga {ln.match.position}</Link>
          {edit && <Button size="sm" variant="ghost" className="h-6 gap-1 px-2 text-xs text-slate-500" onClick={() => onMatch(ln.id, null, null)} data-testid={`supplier-line-unmatch-${ln.id}`}><Unlink className="h-3 w-3" />Scollega</Button>}</div>
      ) : edit && (
        <div className="mt-1 space-y-1">
          {(sug || []).map((s) => (
            <button key={s.order_line_id} type="button" onClick={() => onMatch(ln.id, s.order_id, s.order_line_id)} className="block w-full rounded-md border border-dashed border-brand-200 px-2 py-1 text-left text-xs hover:bg-brand-50" data-testid={`supplier-suggest-${ln.id}-${s.order_line_id}`}>
              Proposta: <span className="font-semibold">{s.order_number}</span> · {s.company} · riga {s.position}: {s.description} ({s.quantity}){s.unit_cost != null ? ` · costo previsto ${fmtCurrency(s.unit_cost)}` : ""}</button>))}
          <button type="button" className="text-xs font-semibold text-brand-700" onClick={() => setPick(!pick)} data-testid={`supplier-line-pick-${ln.id}`}>{pick ? "Chiudi ricerca" : "Abbina a un altro ordine…"}</button>
          {pick && <Picker onPick={(o, l) => { setPick(false); onMatch(ln.id, o, l); }} />}
        </div>
      )}
    </li>
  );
};

export const SupplierInvoiceSheet = ({ id, onClose, onChanged }) => {
  const { can } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); if (id) { api.get(`/supplier-invoices/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(errorMessage(e))); } }, [id]);
  const match = async (lid, order_id, order_line_id) => {
    try { setD((await api.put(`/supplier-invoices/${id}/lines/${lid}`, { order_id, order_line_id })).data); toast.success(order_id ? "Riga abbinata all'ordine" : "Abbinamento rimosso"); onChanged(); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const pdf = async () => { try { window.open((await api.get(`/supplier-invoices/${id}/pdf`)).data.url, "_blank", "noopener"); } catch (e) { toast.error(errorMessage(e)); } };
  const inv = d?.invoice;
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="supplier-invoice-sheet">
        <SheetHeader><SheetTitle>{inv ? `${inv.supplier_name} · fatt. ${inv.number || "s.n."}` : "Caricamento…"}</SheetTitle>
          {inv && <SheetDescription>Del {fmtDate(inv.date)} · imponibile {fmtCurrency(inv.amount_net)} · totale {fmtCurrency(inv.amount_gross)}{inv.supplier_vat ? ` · P.IVA ${inv.supplier_vat}` : ""}</SheetDescription>}</SheetHeader>
        {inv && <>
          {inv.supplier_company_id && <Link to={`/fornitori/${inv.supplier_company_id}`} className="mt-1 inline-block text-xs font-semibold text-brand-700 hover:underline" data-testid="supplier-company-link">Apri scheda fornitore →</Link>}
          <Button size="sm" variant="outline" className="mt-3 gap-1" onClick={pdf} data-testid="supplier-invoice-pdf"><FileText className="h-4 w-4" />Apri PDF</Button>
          <ul className="mt-3 divide-y divide-line">{inv.lines.map((ln) => <Line key={ln.id} ln={ln} sug={d.suggestions[ln.id]} edit={can("supplier_invoices:manage")} onMatch={match} />)}</ul>
        </>}
      </SheetContent>
    </Sheet>
  );
};
